// src/lib/analytics.js
// Central analytics utility — wraps gtag so it's safe to call everywhere
// GA4 auto-collects any event name — no registration needed in the dashboard
// Events appear in GA4 → Reports → Engagement → Events after first fire

// Safe gtag wrapper — no-ops if gtag not loaded or consent denied
function track(eventName, params = {}) {
  if (typeof window === 'undefined') return;
  if (!window.gtag) return;
  window.gtag('event', eventName, params);
}

// ── Auth ──────────────────────────────────────────────────────
export const analytics = {
  // User signed up
  signUp: ({ method = 'email' } = {}) =>
    track('sign_up', { method }),

  // User logged in
  login: ({ method = 'email' } = {}) =>
    track('login', { method }),

  // ── Onboarding ───────────────────────────────────────────────
  // User completed the onboarding tour
  onboardingCompleted: ({ stepsSeen }) =>
    track('onboarding_completed', { steps_seen: stepsSeen }),

  // User skipped the onboarding tour
  onboardingSkipped: ({ atStep }) =>
    track('onboarding_skipped', { at_step: atStep }),

  // ── Entries ───────────────────────────────────────────────────
  // User saved an entry
  entrySaved: ({ type, isPublic }) =>
    track('entry_saved', { output_type: type, is_public: isPublic }),

  // User hit the free save limit
  freeLimitHit: () =>
    track('free_limit_hit'),

  // ── Skills ────────────────────────────────────────────────────
  // User ran a skill (web app)
  skillRun: ({ slug, category }) =>
    track('skill_run', { skill_slug: slug, skill_category: category }),

  // User created a skill
  skillCreated: ({ category }) =>
    track('skill_created', { skill_category: category }),

  // User forked a public skill
  skillForked: ({ slug }) =>
    track('skill_forked', { skill_slug: slug }),

  // ── Playbooks (formerly Packs) ───────────────────────────────
  // User installed a playbook
  packInstalled: ({ slug, skillCount }) =>
    track('pack_installed', { pack_slug: slug, skill_count: skillCount }),
  playbookInstalled: ({ slug, skillCount }) =>
    track('pack_installed', { pack_slug: slug, skill_count: skillCount }),

  // User created a playbook
  packCreated: () =>
    track('pack_created'),
  playbookCreated: () =>
    track('pack_created'),

  // ── Upgrade ───────────────────────────────────────────────────
  // User clicked an upgrade/go pro button
  upgradeClicked: ({ source }) =>
    track('upgrade_clicked', { source }),

  // User started Stripe checkout
  checkoutStarted: ({ plan, billing }) =>
    track('checkout_started', { plan, billing_cycle: billing }),

  // ── Chrome extension ─────────────────────────────────────────
  // User connected the Chrome extension
  extensionConnected: () =>
    track('extension_connected'),

  // ── Teams ─────────────────────────────────────────────────────
  // User created a team
  teamCreated: ({ plan }) =>
    track('team_created', { team_plan: plan }),

  // User accepted a team invite
  teamJoined: () =>
    track('team_joined'),

  // ── CLI / VS Code ─────────────────────────────────────────────
  // User generated an API key (implies CLI/extension intent)
  apiKeyGenerated: () =>
    track('api_key_generated'),
};
